import { useCallback, useEffect, useState } from "react";
import { fetchDealers } from "../../../../services/dealerCoverageApi";
import AdminTableContainer from "../layout/AdminTableContainer";

const FILTER_DEFAULTS = {
  search: "",
  countsTowardGoal: "",
  sourceType: "",
  city: "",
  verificationStatus: "",
  ingestStatus: "",
  needsReview: "",
};

const DealerListAdminPage = ({ onClose, onOpenDealerDetail }) => {
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const query = {};
      for (const [key, value] of Object.entries(filters)) {
        if (value) query[key] = value;
      }
      const data = await fetchDealers(query);
      setDealers(data.dealers || []);
    } catch (err) {
      setErrorMessage(err.message || "Failed to load dealers");
      setDealers([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = (profileKey) => {
    if (onOpenDealerDetail) {
      onOpenDealerDetail(profileKey);
      return;
    }
    window.location.hash = `#/admin/automotive-dealer-detail/${encodeURIComponent(profileKey)}`;
  };

  return (
    <div className="admin-page automotive-dealer-list-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Automotive · Dealer Registry</p>
          <h1>Dealer list</h1>
          <p className="muted">Filterable registry view with goal eligibility and completeness scores.</p>
        </div>
        <div className="admin-page__actions">
          <a className="admin-btn admin-btn--ghost" href="#/admin/automotive-dealer-coverage">
            Coverage dashboard
          </a>
          {onClose ? (
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      <section className="admin-filters admin-filters--inline">
        <input
          type="search"
          placeholder="Search dealer name…"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
        />
        <select
          value={filters.countsTowardGoal}
          onChange={(e) => setFilters((f) => ({ ...f, countsTowardGoal: e.target.value }))}
        >
          <option value="">All goal status</option>
          <option value="true">Counts toward goal</option>
          <option value="false">Excluded</option>
        </select>
        <select
          value={filters.sourceType}
          onChange={(e) => setFilters((f) => ({ ...f, sourceType: e.target.value }))}
        >
          <option value="">All source types</option>
          <option value="direct_inventory">Direct inventory</option>
          <option value="marketplace_profile">Marketplace</option>
          <option value="brand_locator">Brand locator</option>
          <option value="manual_verified">Manual verified</option>
          <option value="social_profile">Social</option>
          <option value="directory_profile">Directory</option>
        </select>
        <input
          type="text"
          placeholder="City"
          value={filters.city}
          onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
        />
        <select
          value={filters.needsReview}
          onChange={(e) => setFilters((f) => ({ ...f, needsReview: e.target.value }))}
        >
          <option value="">Review state</option>
          <option value="true">Needs review</option>
        </select>
        <button type="button" className="admin-btn admin-btn--secondary admin-btn--compact" onClick={load}>
          Apply
        </button>
      </section>

      {errorMessage ? <p className="admin-error">{errorMessage}</p> : null}
      {loading ? <p className="muted">Loading dealers…</p> : null}

      <AdminTableContainer>
        <h3>{dealers.length} dealers</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Dealer</th>
              <th>City</th>
              <th>Source type</th>
              <th>Goal</th>
              <th>Verification</th>
              <th>Ingest</th>
              <th>Sales</th>
              <th>Completeness</th>
              <th>Health</th>
              <th>Stale</th>
              <th>Presences</th>
            </tr>
          </thead>
          <tbody>
            {dealers.map((d) => (
              <tr key={d.profileKey}>
                <td>
                  <button type="button" className="admin-link-btn" onClick={() => openDetail(d.profileKey)}>
                    {d.displayName}
                  </button>
                </td>
                <td>{d.primaryCity || d.cityBucket || "—"}</td>
                <td>{d.primarySourceType || "—"}</td>
                <td>{d.countsTowardGoal ? "Yes" : "No"}</td>
                <td>{d.verificationStatus}</td>
                <td>{d.ingestStatus}</td>
                <td>
                  {[d.newVehicleSales && "N", d.usedVehicleSales && "U", d.certifiedPreowned && "CPO"]
                    .filter(Boolean)
                    .join("+") || "—"}
                </td>
                <td>{d.completenessScore ?? "—"}</td>
                <td>
                  {d.dealerHealthScore != null
                    ? `${d.dealerHealthScore}${d.dealerHealthBand ? ` (${d.dealerHealthBand})` : ""}`
                    : "—"}
                </td>
                <td>{d.staleFlag ? "Yes" : "—"}</td>
                <td>{d.sourcePresenceCount ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminTableContainer>
    </div>
  );
};

export default DealerListAdminPage;
